import { Component, inject, InjectionToken, Type, ViewContainerRef } from '@angular/core'
import { Route } from '@angular/router'

import { BOOTSTRAP_DATA } from 'api'
import { loginRedirectGuard, NextRedirectorService } from 'common/util'
import { PanelRootComponent } from 'panel'

import { AppRootComponent } from './appRoot.component'

export const PANEL_ROOT_COMPONENT = new InjectionToken<Type<unknown>>('ROOT_PANEL_COMPONENT')
export const APP_ROOT_COMPONENT = new InjectionToken<Type<unknown>>('APP_ROOT_COMPONENT')

/**
 * This component dynamically replaces itself with the appropriate root component
 * based on whether the user has initially landed on `/panel` or a "normal" route.
 * Only used for the "non-special" routes where the user is already authenticated.
 *
 * `ViewContainerRef` is used to avoid adding a separate wrapper DOM element,
 * which would interfere with existing styles. The real shell is rendered as a
 * sibling to this component.
 */
@Component({
  selector: 'app-root-outlet',
  template: ``,
  providers: [
    // Providers because we're mocking these in the tests
    { provide: PANEL_ROOT_COMPONENT, useValue: PanelRootComponent },
    { provide: APP_ROOT_COMPONENT, useValue: AppRootComponent },
  ],
})
export class AppRootOutletComponent {
  readonly #vcr = inject(ViewContainerRef)
  readonly #nextRedirector = inject(NextRedirectorService)
  readonly #bootstrapData = inject(BOOTSTRAP_DATA)
  readonly #panelRootComponent = inject(PANEL_ROOT_COMPONENT)
  readonly #appRootComponent = inject(APP_ROOT_COMPONENT)

  async ngOnInit () {
    this.#vcr.clear()

    // EW-8322: router navigation must complete before loading child view
    // which contains router outlet
    await this.#nextRedirector.maybeFollowPendingRedirect()

    if (this.#bootstrapData.isPanel) {
      this.#vcr.createComponent(this.#panelRootComponent)
      return
    }

    this.#vcr.createComponent(this.#appRootComponent)
  }

  static ROUTE: Route = {
    path: '',
    component: AppRootOutletComponent,
    canActivate: [loginRedirectGuard],
  }
}
